/**
 * Игровой стор одиночной жизни поверх `@life/engine`.
 *
 * Движок вызывается только через публичный API (`createLife`, `advanceYear`,
 * `resolveDecision`, `availableFocus`, `chooseOption`); `Life` клиент не
 * мутирует — только заменяет целиком новым объектом из движка. Исключение
 * по плану: пометки журнала (`starred`, `params.thought`), имя при
 * рождении и флаги эпитафии (`lastWords`, `keyYears`) — они тоже пишутся
 * копированием, без изменения старого объекта.
 *
 * Случайность года — `createRng(seed).fork('year/<age>')`, как в
 * `simulateLife`, поэтому та же жизнь с тем же seed и теми же ответами
 * воспроизводится на любом клиенте. `Math.random` здесь нет; seed жизни —
 * один раз `crypto.randomUUID()` при рождении.
 *
 * Мини-игры (план 2b, Task 8): `pendingMinigame` — открытая мини-игра.
 * Решение с мини-игрой (`Trigger.decision`) сначала показывает её: исход
 * применяется к жизни через `applyMinigame` движка, а `decisionOption`
 * уходит в `answer()`; на `move.abroad` игры идут по очереди (чемодан,
 * затем граница), «остаться» в первой же — ответ сразу. Триггеры `age` и
 * `event` проверяются после тика (не больше одной игры в год, пока нет
 * решения), их исход ждёт следующего тика в `minigameQueued` как
 * `YearInput.minigame`. Автопилот мини-игры не открывает: играет по
 * `expected()`, как и «Пропустить» и как просроченная (> 1 ч) игра при
 * загрузке. Казино (раздел E) закрыто при `settings.gamblingEnabled = false`.
 *
 * Ход года (план 2d, Task 5; core-loop-v2 §2): `phase` — такт хода
 * (`morning` → `actions`/`decision` → `ending` → снова `morning`).
 * `act(place, action)` — дело места: `runPlaceAction` даёт либо исход,
 * который применяется к жизни сразу через `applyActions` движка (энергия −1,
 * визит засчитан, запись в журнале — немедленно), либо мини-игру, которую
 * открывает `Host`; её исход идёт тем же путём (`PendingMinigame.place`).
 * Действия копятся в `pendingActions` до конца года. `endYear()` — тик
 * движка от жизни, в которой действия уже применены (повторно в
 * `YearInput.actions` они не передаются — иначе применились бы дважды);
 * автопилот тратит энергию по `expectedPolicy` движка — как и режим
 * «Наблюдать» (Task 7): там нет меню мест, год идёт по часам, и дела за
 * игрока делает та же политика (фокус и решения остаются за ним). Дельты года
 * (`lastDeltas`) считаются между утром прошлого и утром нового года.
 *
 * Мечта (план 2d, Task 6): `life.pendingDream` держит год, как решение;
 * `chooseDream` применяет выбор сразу через `resolveDream` движка (без
 * `YearInput.dream`: ответ не должен теряться при перезагрузке, а
 * `pendingDream` уже в сохранении). Экран `dossier` — «Личное дело» (H1).
 */
import type { Dataset } from '@life/data';
import {
  advanceYear,
  applyActions,
  applyMinigame,
  availableFocus,
  chooseOption,
  createLife,
  createRng,
  expectedPolicy,
  resolveDecision,
  resolveDream,
  YEAR_SUMMARY_KEY,
  type ActionInput,
  type Decision,
  type DreamId,
  type Focus,
  type Life,
  type LogEntry,
  type MinigameOutcome,
  type Rng,
  type YearInput,
} from '@life/engine';
import { mergeOutcomes, minigameDef, MINIGAMES, runPlaceAction, type MinigameDef, type PlaceId, type Trigger } from '@life/minigames';
import { computed, signal, type ReadonlySignal, type Signal } from '@preact/signals';
import {
  availableFor,
  decisionMinigames,
  expectedOf,
  isStale,
  minigameRng,
  tickMinigame,
  type PendingMinigame,
  type PlaceAction,
  type QueuedMinigame,
  type TriggerOptions,
} from './minigame';
import { DEFAULT_SETTINGS, type Settings } from './settings';
import { save, type SaveV1 } from './storage';
import { placeOptions, yearDeltas, type Phase, type YearDeltas } from './turn';

export type Screen = 'start' | 'birth' | 'life' | 'death' | 'dossier';

/** Больше стольких смен фокуса за год — «метания», −1 счастья в движке. */
export const RESTLESS_THRESHOLD = 3;
export const FLAG_LAST_WORDS = 'lastWords';
export const FLAG_KEY_YEARS = 'keyYears';
/** Сколько завершённых жизней хранится на полке (дублирует storage.SHELF_MAX для среза в сторе). */
const SHELF_MAX = 10;

export interface GameState {
  screen: Signal<Screen>;
  settings: Signal<Settings>;
  life: Signal<Life | null>;
  /** Завершённые жизни, новые в конце. */
  shelf: Signal<Life[]>;
  /** Решение, ждущее ответа игрока; null — отвечено или нет решения. */
  pending: Signal<Decision | null>;
  autopilot: Signal<boolean>;
  /** Записи журнала последнего тика (и ответа на решение). */
  lastEntries: Signal<LogEntry[]>;
  /** Сколько раз игрок менял фокус с прошлого тика. */
  focusChanges: Signal<number>;
  /** Фокус, выбранный игроком на следующий год. */
  focus: Signal<Focus>;
  /** Открытая мини-игра; null — нет. */
  pendingMinigame: Signal<PendingMinigame | null>;
  /** Сыгранный исход, ждущий следующего тика (триггеры age/event/envelope). */
  minigameQueued: Signal<QueuedMinigame | null>;
  /** Такт хода года; `decision` — такт действий, пока решение или мини-игра ждут ответа. */
  phase: ReadonlySignal<Phase>;
  /** Действия этого года по местам (уже применены к жизни); пусто после тика. */
  pendingActions: Signal<ActionInput[]>;
  /** Энергия, оставшаяся на год (`life.energy`). */
  energyLeft: ReadonlySignal<number>;
  /** Дельты прошедшего года для утренней сводки; null — первый год или после загрузки. */
  lastDeltas: Signal<YearDeltas | null>;
}

export interface GameActions {
  born(name?: string): void;
  rename(name: string): void;
  startLife(): void;
  setFocus(f: Focus): void;
  answer(optionId: string): void;
  /** Выбрать мечту из `life.pendingDream` (core-loop-v2 §4): применяется сразу, как ответ на решение. */
  chooseDream(id: DreamId): void;
  /** «Личное дело» (H1): экран-документ поверх жизни; `closeDossier` возвращает на экран жизни. */
  openDossier(): void;
  closeDossier(): void;
  payDebt(amount: number): void;
  /** Прожить год (тик движка); false — год не пошёл (решение/мини-игра ждут, жизнь окончена). */
  tick(): boolean;
  /** Утро года: сводка прошлого года, фаза `morning`. */
  beginYear(): void;
  /** «К делам»: фаза `actions`. */
  startActions(): void;
  /**
   * Сходить в место: исход применяется сразу (или открывается мини-игра).
   * false — место/дело недоступно (нет энергии, исчерпано, условия), мини-игра уже открыта.
   */
  act(place: PlaceId, action: string): boolean;
  /** «Следующий год»: тик и новое утро; false — год не пошёл (см. `tick`). */
  endYear(): boolean;
  starYear(age: number): void;
  addThought(age: number, text: string): void;
  setEpitaph(words: string, keyYears: number[]): void;
  bornAgain(): void;
  persist(): void;
  /** Доступные мини-игры для триггера с учётом настроек (раздел E, тяжёлое). */
  minigamesAvailable(trigger: Trigger): MinigameDef[];
  /**
   * Открыть мини-игру по триггеру (`id` — конкретную, иначе первую доступную).
   * false — нечего открывать: нет доступных, уже открыта или на год уже есть исход.
   */
  openMinigame(trigger: Trigger, id?: string): boolean;
  /** Завершить открытую мини-игру её исходом (из `def.play` или `def.expected`). */
  finishMinigame(outcome: MinigameOutcome): void;
  /** «Пропустить»: ожидаемый исход. */
  skipMinigame(): void;
}

export type Game = GameState & GameActions;

/** Seed новой жизни: `crypto.randomUUID()` доступен только в защищённом контексте (HTTPS/localhost),
 *  поэтому по HTTP используем `getRandomValues` — 128 бит в hex. */
function newSeed(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Rng года: тот же fork, что и в `simulateLife`, чтобы seed давал ту же жизнь. */
function yearRng(life: Life): Rng {
  return createRng(life.seed).fork(`year/${life.age}`);
}

function screenFor(life: Life | null): Screen {
  if (life === null) return 'start';
  return life.alive ? 'life' : 'death';
}

/** Запись года для пометок: итог года, иначе первая запись этого возраста. */
function yearEntryIndex(log: LogEntry[], age: number): number {
  const summary = log.findIndex((e) => e.age === age && e.key === YEAR_SUMMARY_KEY);
  return summary >= 0 ? summary : log.findIndex((e) => e.age === age);
}

function withEntry(life: Life, age: number, patch: (e: LogEntry) => LogEntry): Life {
  const i = yearEntryIndex(life.log, age);
  if (i < 0) return life;
  const log = life.log.slice();
  log[i] = patch(log[i]!);
  return { ...life, log };
}

export interface GameOptions {
  /** Фиксированный seed для всех новых жизней (E2E, воспроизведение); иначе `crypto.randomUUID()`. */
  seed?: string;
}

export function createGame(ds: Dataset, initial?: SaveV1 | null, storage?: Storage | null, options: GameOptions = {}): Game {
  const initialLife = initial?.life ?? null;
  const screen = signal<Screen>(screenFor(initialLife));
  const settings = signal<Settings>(initial?.settings ?? { ...DEFAULT_SETTINGS });
  const life = signal<Life | null>(initialLife);
  const shelf = signal<Life[]>(initial?.shelf.slice(-SHELF_MAX) ?? []);
  const pending = signal<Decision | null>(initialLife?.pendingDecision ?? null);
  const autopilot = signal(false);
  const lastEntries = signal<LogEntry[]>([]);
  const focusChanges = signal(0);
  const focus = signal<Focus>(initialLife?.focus ?? 'play');
  const pendingMinigame = signal<PendingMinigame | null>(null);
  const minigameQueued = signal<QueuedMinigame | null>(initialLife?.alive ? (initial?.minigameQueued ?? null) : null);
  const stage = signal<Exclude<Phase, 'decision'>>('morning');
  const pendingActions = signal<ActionInput[]>([]);
  const lastDeltas = signal<YearDeltas | null>(null);
  const energyLeft = computed(() => life.value?.energy ?? 0);
  const phase = computed<Phase>(() =>
    stage.value === 'actions' && (pending.value !== null || pendingMinigame.value !== null) ? 'decision' : stage.value,
  );
  let debtQueued = 0;
  /** Жизнь на утро текущего года — база дельт следующей сводки. */
  let morningLife: Life | null = null;

  function triggerOpts(): TriggerOptions {
    const { gamblingEnabled, skipHeavy } = settings.value;
    return { gamblingEnabled, skipHeavy };
  }

  function open(def: MinigameDef, trigger: Trigger, current: Life, played: string[] = [], place?: PlaceAction): void {
    const state = def.setup(current, ds, minigameRng(current, def.id));
    const next: PendingMinigame = { id: def.id, state, openedAt: Date.now(), trigger, played };
    if (place) next.place = place;
    pendingMinigame.value = next;
  }

  /**
   * Мини-игра висящего решения, ещё не сыгранная для него. `played` — уже
   * сыгранные для этого решения id; он уходит в `PendingMinigame.played` и
   * с ним в сохранение, чтобы перезагрузка между двумя играми одного
   * решения не повторяла первую.
   */
  function offerDecisionMinigame(current: Life, played: string[] = []): void {
    const decision = current.pendingDecision;
    if (decision === null || !current.alive || autopilot.value || pendingMinigame.value !== null) return;
    const def = decisionMinigames(current, ds, triggerOpts()).find((d) => !played.includes(d.id));
    if (def) open(def, { kind: 'decision', decision: decision.kind }, current, played);
  }

  function setLife(next: Life): void {
    life.value = next;
    pending.value = next.pendingDecision;
    offerDecisionMinigame(next);
  }

  function minigamesAvailable(trigger: Trigger): MinigameDef[] {
    const current = life.value;
    if (current === null) return [];
    return availableFor(trigger, current, ds, triggerOpts());
  }

  function openMinigame(trigger: Trigger, id?: string): boolean {
    const current = life.value;
    if (current === null || !current.alive || pendingMinigame.value !== null || minigameQueued.value !== null) return false;
    const defs = minigamesAvailable(trigger);
    const def = id === undefined ? defs[0] : defs.find((d) => d.id === id);
    if (!def) return false;
    open(def, trigger, current);
    return true;
  }

  function finishMinigame(outcome: MinigameOutcome): void {
    const current = life.value;
    const open_ = pendingMinigame.value;
    if (current === null || open_ === null) return;
    pendingMinigame.value = null;
    if (!current.alive) return;

    if (open_.place) {
      applyAction({ place: open_.place.place, action: open_.place.action, outcome: mergeOutcomes(open_.place.base ?? {}, outcome) });
      // Дело при висящем решении (собеседование с биржи, очередь в поликлинике) отвечает на него.
      if (outcome.decisionOption !== undefined && pending.value !== null) answer(outcome.decisionOption);
      return;
    }

    if (open_.trigger.kind !== 'decision') {
      minigameQueued.value = { id: open_.id, outcome };
      return;
    }

    // Исход решения — сразу в жизнь, вариант — в ответ.
    const decision = pending.value;
    const played = applyMinigame(current, outcome);
    life.value = { ...played.life, log: [...played.life.log, ...played.entries] };
    lastEntries.value = [...lastEntries.value, ...played.entries];
    if (decision === null) return;

    // «Остаться» (stayOption состояния) — следующие игры этого решения не нужны.
    const stay = (open_.state as { stayOption?: unknown } | null)?.stayOption;
    const stayed = typeof stay === 'string' && outcome.decisionOption === stay;
    if (!stayed) {
      offerDecisionMinigame(life.value, [...open_.played, open_.id]);
      if (pendingMinigame.value !== null) return;
    }
    answer(outcome.decisionOption ?? decision.timeoutOption);
  }

  function skipMinigame(): void {
    const current = life.value;
    const open_ = pendingMinigame.value;
    if (current === null || open_ === null) return;
    finishMinigame(expectedOf(minigameDef(open_.id), open_.state, current));
  }

  function born(name?: string): void {
    const seed = options.seed ?? newSeed();
    const { epoch, drawMode } = settings.value;
    const next = createLife(ds, name === undefined ? { epoch, drawMode, seed } : { epoch, drawMode, seed, name });
    setLife(next);
    focus.value = next.focus;
    focusChanges.value = 0;
    debtQueued = 0;
    lastEntries.value = [];
    pendingMinigame.value = null;
    minigameQueued.value = null;
    pendingActions.value = [];
    lastDeltas.value = null;
    morningLife = null;
    stage.value = 'morning';
    screen.value = 'birth';
  }

  function rename(name: string): void {
    const current = life.value;
    if (current === null) return;
    life.value = { ...current, name };
  }

  function startLife(): void {
    if (life.value === null) return;
    screen.value = screenFor(life.value);
  }

  function setFocus(f: Focus): void {
    const current = life.value;
    if (current === null || !current.alive) return;
    if (!availableFocus(current).includes(f)) return;
    if (focus.value === f) return;
    focus.value = f;
    focusChanges.value += 1;
  }

  function answer(optionId: string): void {
    const current = life.value;
    const decision = pending.value;
    if (current === null || !current.alive || decision === null) return;
    const rng = yearRng(current).fork('answer');
    let result = resolveDecision(ds, current, decision, optionId, rng);
    if (result.rejected) {
      result = resolveDecision(ds, current, decision, chooseOption(current, decision), rng, { auto: true });
      if (result.rejected) return;
    }
    const next: Life = { ...result.life, log: [...result.life.log, ...result.entries] };
    life.value = next;
    pending.value = null;
    lastEntries.value = [...lastEntries.value, ...result.entries];
  }

  function chooseDream(id: DreamId): void {
    const current = life.value;
    if (current === null || !current.alive || current.pendingDream === null || !current.pendingDream.includes(id)) return;
    const result = resolveDream(current, id);
    life.value = { ...result.life, log: [...result.life.log, ...result.entries] };
    lastEntries.value = [...lastEntries.value, ...result.entries];
  }

  function openDossier(): void {
    if (life.value === null) return;
    screen.value = 'dossier';
  }

  function closeDossier(): void {
    if (screen.value !== 'dossier') return;
    screen.value = screenFor(life.value);
  }

  function payDebt(amount: number): void {
    if (!(amount > 0)) return;
    debtQueued += amount;
  }

  function tick(): boolean {
    const current = life.value;
    if (current === null || !current.alive) return false;
    if (pending.value !== null && !autopilot.value) return false;
    // Предложенная мечта ждёт выбора игрока; автопилот берёт первую (движок).
    if (current.pendingDream !== null && current.pendingDream.length > 0 && !autopilot.value) return false;
    // Открытая мини-игра держит год; автопилот доигрывает её ожидаемым исходом.
    if (pendingMinigame.value !== null) {
      if (!autopilot.value) return false;
      skipMinigame();
      if (pendingMinigame.value !== null || life.value !== current) {
        // Исход решения уже изменил жизнь — год пойдёт со следующего тика.
        return false;
      }
    }

    const input: YearInput = {
      skipHeavy: settings.value.skipHeavy,
      restless: focusChanges.value > RESTLESS_THRESHOLD,
    };
    if (!autopilot.value) input.focus = focus.value;
    if (autopilot.value || settings.value.mode === 'observe') {
      // Автопилот — и наблюдатель, у которого нет меню мест, — тратят оставшуюся
      // энергию по ожидаемой политике движка (калибровка этапа 1).
      const policy = expectedPolicy(current, ds);
      if (policy.length > 0) input.actions = policy;
    }
    if (debtQueued > 0) input.payDebt = debtQueued;
    const queued = minigameQueued.value;
    if (queued !== null) input.minigame = queued;
    const result = advanceYear(ds, current, input, yearRng(current));
    minigameQueued.value = null;
    setLife(result.life);
    lastEntries.value = result.entries;
    focusChanges.value = 0;
    focus.value = result.life.focus;
    debtQueued = 0;
    pendingActions.value = [];

    // Триггеры года (событие, возраст): одна игра, пока нет решения; автопилот — по ожиданию.
    if (result.life.alive && result.life.pendingDecision === null && pendingMinigame.value === null) {
      const hit = tickMinigame(result.life, result.entries, ds, triggerOpts());
      if (hit) {
        if (autopilot.value) {
          const state = hit.def.setup(result.life, ds, minigameRng(result.life, hit.def.id));
          minigameQueued.value = { id: hit.def.id, outcome: expectedOf(hit.def, state, result.life) };
        } else {
          open(hit.def, hit.trigger, result.life);
        }
      }
    }

    if (!result.life.alive) {
      shelf.value = [...shelf.value, result.life].slice(-SHELF_MAX);
      // Первая жизнь кончилась — туториал больше не показываем, даже если не все подсказки закрыты.
      if (!settings.value.hintsSeen) settings.value = { ...settings.value, hintsSeen: true };
      screen.value = 'death';
    }
    return true;
  }

  function beginYear(): void {
    const current = life.value;
    stage.value = 'morning';
    morningLife = current;
  }

  function startActions(): void {
    if (morningLife === null) morningLife = life.value;
    stage.value = 'actions';
  }

  /** Применить действие к жизни сразу: энергия −1, визит, записи в журнал и в `lastEntries`. */
  function applyAction(action: ActionInput): boolean {
    const current = life.value;
    if (current === null || !current.alive) return false;
    const applied = applyActions(current, [action]);
    if (applied.rejected > 0) return false;
    life.value = { ...applied.life, log: [...applied.life.log, ...applied.entries] };
    lastEntries.value = [...lastEntries.value, ...applied.entries];
    pendingActions.value = [...pendingActions.value, action];
    return true;
  }

  function act(place: PlaceId, action: string): boolean {
    const current = life.value;
    if (current === null || !current.alive || pendingMinigame.value !== null) return false;
    const rng = createRng(current.seed).fork(`place/${current.age}/${place}/${action}/${current.visits[place] ?? 0}`);
    const run = runPlaceAction(place, action, current, ds, rng, placeOptions(current, settings.value));
    if (run === null) return false;
    if (run.kind === 'minigame') {
      const placeAction: PlaceAction = { place, action };
      if (run.outcome) placeAction.base = run.outcome;
      open(minigameDef(run.id), { kind: 'place', place }, current, [], placeAction);
      return true;
    }
    return applyAction({ place, action, outcome: run.outcome });
  }

  function endYear(): boolean {
    const current = life.value;
    if (current === null || !current.alive) return false;
    const before = morningLife ?? current;
    const prevStage = stage.value;
    stage.value = 'ending';
    const advanced = tick();
    if (!advanced) {
      stage.value = prevStage === 'ending' ? 'actions' : prevStage;
      return false;
    }
    lastDeltas.value = yearDeltas(before, life.value!);
    beginYear();
    return true;
  }

  function starYear(age: number): void {
    const current = life.value;
    if (current === null) return;
    life.value = withEntry(current, age, (e) => ({ ...e, starred: !e.starred }));
  }

  function addThought(age: number, text: string): void {
    const current = life.value;
    if (current === null) return;
    life.value = withEntry(current, age, (e) => ({ ...e, params: { ...e.params, thought: text } }));
  }

  function setEpitaph(words: string, keyYears: number[]): void {
    const current = life.value;
    if (current === null) return;
    life.value = {
      ...current,
      flags: { ...current.flags, [FLAG_LAST_WORDS]: words, [FLAG_KEY_YEARS]: keyYears.join(',') },
    };
  }

  function bornAgain(): void {
    life.value = null;
    pending.value = null;
    autopilot.value = false;
    lastEntries.value = [];
    focusChanges.value = 0;
    debtQueued = 0;
    pendingMinigame.value = null;
    minigameQueued.value = null;
    pendingActions.value = [];
    lastDeltas.value = null;
    morningLife = null;
    stage.value = 'morning';
    screen.value = 'start';
  }

  function persist(): void {
    const data: SaveV1 = {
      v: 1,
      settings: settings.value,
      life: life.value,
      shelf: shelf.value,
      savedAt: Date.now(),
      pendingMinigame: pendingMinigame.value,
      minigameQueued: minigameQueued.value,
    };
    if (storage === undefined) save(data);
    else save(data, storage);
  }

  // Восстановление: сохранённая мини-игра (просроченная — ожидаемым исходом), иначе игра висящего решения.
  const saved = initial?.pendingMinigame ?? null;
  if (initialLife?.alive && saved !== null && MINIGAMES.some((d) => d.id === saved.id)) {
    if (isStale(saved)) {
      pendingMinigame.value = saved;
      skipMinigame();
    } else {
      pendingMinigame.value = saved;
    }
  }
  // По актуальной жизни: просроченная игра решения могла уже ответить на него.
  if (life.value !== null && pendingMinigame.value === null) offerDecisionMinigame(life.value);

  return {
    screen,
    settings,
    life,
    shelf,
    pending,
    autopilot,
    lastEntries,
    focusChanges,
    focus,
    pendingMinigame,
    minigameQueued,
    phase,
    pendingActions,
    energyLeft,
    lastDeltas,
    born,
    rename,
    startLife,
    setFocus,
    answer,
    chooseDream,
    openDossier,
    closeDossier,
    payDebt,
    tick,
    beginYear,
    startActions,
    act,
    endYear,
    starYear,
    addThought,
    setEpitaph,
    bornAgain,
    persist,
    minigamesAvailable,
    openMinigame,
    finishMinigame,
    skipMinigame,
  };
}
